/**
 * TeacherLessonRecapPage — closes the classroom session.
 *
 * Per Teacher Mode Build Spec §24C / §17 / §18. Reads the cached recap that
 * the backend generated when the run was completed (idempotent — refresh
 * never re-hits the LLM). Lets the learner mark their homework done and
 * choose "Next lesson" or "Back home".
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CaretLeft, CheckCircle, Confetti, Notebook, ChalkboardTeacher,
  Lightning, GraduationCap, ArrowRight, SpinnerGap,
} from '@phosphor-icons/react';
import { http, apiErrorMessage } from '@/lib/api';

export default function TeacherLessonRecapPage() {
  const { runId } = useParams();
  const navigate = useNavigate();
  const [run, setRun] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [homeworkRow, setHomeworkRow] = useState(null);
  const [hwBusy, setHwBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data: runData } = await http.get(`/teacher/runs/${runId}`);
        let workingRun = runData.run;
        const lessonDoc = runData.lesson;
        // If the run isn't completed yet (user navigated here directly),
        // finalize it first so we have recap text.
        if (!workingRun?.completed_at) {
          const completed = await http.post(`/teacher/runs/${runId}/complete`, {});
          workingRun = completed.data.run;
        }
        setRun(workingRun);
        setLesson(lessonDoc);

        // Pull the most recent homework row for this run.
        const { data: notebook } = await http.get('/teacher/notebook');
        const hw = (notebook.homework || []).find(
          (h) => h.session_run_id === runId,
        );
        setHomeworkRow(hw || null);
      } catch (e) {
        setError(apiErrorMessage(e));
      }
    })();
  }, [runId]);

  async function toggleHomework() {
    if (!homeworkRow || hwBusy) return;
    setHwBusy(true);
    try {
      await http.post(
        `/teacher/homework/${homeworkRow.id}/complete`,
        { is_completed: !homeworkRow.is_completed },
      );
      setHomeworkRow({
        ...homeworkRow,
        is_completed: !homeworkRow.is_completed,
      });
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setHwBusy(false);
    }
  }

  if (!run || !lesson) {
    return (
      <div className="lf-shell flex items-center justify-center" data-testid="teacher-recap-loading">
        <SpinnerGap size={28} className="animate-spin text-[var(--lf-primary)]" />
      </div>
    );
  }

  return (
    <div className="lf-shell flex flex-col px-6 py-6" data-testid="teacher-recap-page">
      <div className="flex items-center gap-2">
        <Link
          to="/home"
          className="grid h-10 w-10 place-items-center rounded-full border border-[var(--lf-border)] bg-[var(--lf-surface)]"
          data-testid="teacher-recap-back"
        >
          <CaretLeft size={18} />
        </Link>
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--lf-text-muted)]">
            Teacher mode
          </div>
          <h1 className="text-xl font-bold">Lesson recap</h1>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-5 lf-card p-5 bg-gradient-to-br from-[#FFFFFF] to-[#FFF5EE]"
        data-testid="teacher-recap-hero"
      >
        <div className="flex items-center gap-2">
          <Confetti size={20} weight="duotone" className="text-[var(--lf-primary)]" />
          <div className="text-xs uppercase tracking-widest text-[var(--lf-primary)]">
            Class dismissed
          </div>
        </div>
        <h2 className="mt-1 text-2xl font-bold leading-tight" data-testid="teacher-recap-lesson-title">
          {lesson.title}
        </h2>
        <p className="mt-1 text-sm text-[var(--lf-text-muted)]">
          {lesson.objective}
        </p>
      </motion.div>

      {/* Recap text */}
      <Section
        icon={<ChalkboardTeacher size={16} weight="duotone" />}
        label="What you learned"
        testid="teacher-recap-summary"
      >
        <p className="text-sm leading-relaxed">
          {run.recap_text || `Today you practiced: ${lesson.objective}`}
        </p>
      </Section>

      {/* Vocab covered during the run */}
      {run.used_vocab?.length ? (
        <Section
          icon={<GraduationCap size={16} weight="duotone" />}
          label="Phrases practiced"
          testid="teacher-recap-vocab"
        >
          <div className="flex flex-wrap gap-1.5">
            {run.used_vocab.map((w) => (
              <span
                key={w}
                className="rounded-full border border-[var(--lf-border)] bg-[var(--lf-surface)] px-2.5 py-1 text-xs"
                data-testid="teacher-recap-vocab-chip"
              >
                {w}
              </span>
            ))}
          </div>
        </Section>
      ) : null}

      {/* Phase 2 — Corrections list (shown when correction phase ran) */}
      {run.corrections?.length ? (
        <Section
          icon={<Lightning size={16} weight="duotone" />}
          label="Corrections"
          testid="teacher-recap-corrections"
        >
          <div className="space-y-1.5">
            {run.corrections.map((c, i) => (
              <div
                key={i}
                className="rounded-2xl border border-rose-200 bg-rose-50 p-2.5 text-sm"
                data-testid="teacher-recap-correction-row"
              >
                {c.original ? (
                  <div className="line-through text-rose-500 text-xs">{c.original}</div>
                ) : null}
                <div className="font-semibold text-rose-900">{c.corrected}</div>
              </div>
            ))}
          </div>
        </Section>
      ) : null}

      {/* Phase 2 — Exit Ticket result (shown when exit_ticket phase ran) */}
      {run.exit_ticket_attempts?.length ? (
        <Section
          icon={<CheckCircle size={16} weight="duotone" />}
          label="Exit ticket"
          testid="teacher-recap-exit-ticket-result"
        >
          <div
            className={`rounded-2xl border p-3 text-sm ${
              run.exit_ticket_passed
                ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                : 'border-rose-200 bg-rose-50 text-rose-900'
            }`}
          >
            <div className="font-bold">
              {run.exit_ticket_passed ? '✓ Passed' : '✗ Try again next time'}
              <span className="ml-1 text-xs font-medium">
                ({Math.round((run.exit_ticket_attempts.at(-1)?.score || 0) * 100)}% best match)
              </span>
            </div>
            <div className="mt-1 text-xs">
              {run.exit_ticket_attempts.length} attempt{run.exit_ticket_attempts.length === 1 ? '' : 's'}
            </div>
          </div>
        </Section>
      ) : null}

      {/* Exit ticket */}
      {run.exit_ticket_text && !run.exit_ticket_attempts?.length ? (
        <Section
          icon={<Lightning size={16} weight="duotone" />}
          label="Final challenge"
          testid="teacher-recap-exit-ticket"
        >
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            {run.exit_ticket_text}
          </div>
        </Section>
      ) : null}

      {/* Homework */}
      {(run.homework_text || homeworkRow) ? (
        <Section
          icon={<Notebook size={16} weight="duotone" />}
          label="Homework"
          testid="teacher-recap-homework"
        >
          <button
            onClick={toggleHomework}
            disabled={!homeworkRow || hwBusy}
            className={`w-full text-left rounded-2xl border p-3 transition-colors ${
              homeworkRow?.is_completed
                ? 'border-emerald-200 bg-emerald-50'
                : 'border-[var(--lf-border)] bg-[var(--lf-surface)]'
            }`}
            data-testid="teacher-recap-homework-toggle"
          >
            <div className="flex items-start gap-2.5">
              <div className={`mt-0.5 grid h-5 w-5 flex-shrink-0 place-items-center rounded-full ${
                homeworkRow?.is_completed
                  ? 'bg-emerald-600 text-white'
                  : 'border border-[var(--lf-border)]'
              }`}>
                {homeworkRow?.is_completed ? <CheckCircle size={14} weight="fill" /> : null}
              </div>
              <div className="flex-1">
                <div className={`text-sm ${
                  homeworkRow?.is_completed ? 'text-emerald-900 line-through' : ''
                }`}>
                  {run.homework_text || homeworkRow?.instruction}
                </div>
                <div className="mt-1 text-xs text-[var(--lf-text-muted)]">
                  {homeworkRow?.is_completed ? 'Marked complete · tap to undo' : 'Tap when finished'}
                </div>
              </div>
            </div>
          </button>
        </Section>
      ) : null}

      {error ? (
        <div className="mt-3 text-sm text-[var(--lf-destructive)]" data-testid="teacher-recap-error">
          {error}
        </div>
      ) : null}

      <div className="mt-6 space-y-3">
        <button
          onClick={() => navigate('/teacher/today', { replace: true })}
          className="lf-btn lf-btn-primary w-full"
          data-testid="teacher-recap-next-btn"
        >
          Next lesson <ArrowRight size={14} weight="bold" />
        </button>
        <Link
          to="/teacher/notebook"
          className="lf-btn lf-btn-ghost w-full"
          data-testid="teacher-recap-notebook-btn"
        >
          <Notebook size={14} /> Open notebook
        </Link>
        <Link
          to="/home"
          className="text-center text-xs text-[var(--lf-text-muted)] underline-offset-2 hover:underline block"
          data-testid="teacher-recap-home-link"
        >
          Back to Home
        </Link>
      </div>
    </div>
  );
}

function Section({ icon, label, testid, children }) {
  return (
    <div className="mt-5" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[var(--lf-text-muted)]">
        {icon} {label}
      </div>
      <div className="mt-2">{children}</div>
    </div>
  );
}
